/**
 * @file ServicePageLayout.tsx
 * @summary Composant global
 *
 * Composant transverse utilisé dans plusieurs pages (header, footer, SEO, layout…).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChevronRight, ArrowLeft } from "lucide-react";
import BookingCTA from "@/components/BookingCTA";

interface Crumb { label: string; to?: string; }
interface ServicePageProps {
  breadcrumbs: Crumb[];
  h1: string;
  intro: string; // "En bref" content
  children: ReactNode;
}

const RESAWOD = "https://www.resawod.com/reservation/crossfitchistera";

export default function ServicePageLayout({ breadcrumbs, h1, intro, children }: ServicePageProps) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="w-3 h-3" /> Accueil
            </Link>
            {breadcrumbs.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                {c.to ? <Link to={c.to} className="hover:text-foreground">{c.label}</Link> : <span className="text-foreground">{c.label}</span>}
              </span>
            ))}
          </nav>

          <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">{h1}</h1>

          {/* En bref */}
          <aside className="border-l-4 border-gold bg-charcoal/40 px-5 py-4 mb-10 rounded-r-md">
            <p className="text-sm uppercase tracking-wider text-gold font-heading mb-1">En bref</p>
            <p className="text-foreground/90 leading-relaxed">{intro}</p>
          </aside>

          <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">
            {children}
          </div>

          <BookingCTA />

        </div>
      </main>
      <Footer />
    </div>
  );
}
